import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * A round button with only an icon. `label` says what it does, for screen readers and as its tooltip.
 * `xs` (20px, a thumb's reach around it) beside a figure, `sm` inside popups and rows, `md` in bars and the chat box, `lg` (44px)
 * as the thumb-sized control of a page made for a phone. `grey` for close and menus, `none` for a secondary action, `white` for one that stands on its own beside a
 * field (the mic), `black` for the one main action (send, play), `red` while something is in progress that this stops
 * (recording).
 */
export function IconButton({
  label,
  size = "md",
  fill = "grey",
  className = "",
  children,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & {
  label: string;
  size?: "xs" | "sm" | "md" | "lg";
  fill?: "grey" | "none" | "white" | "black" | "red";
  children: ReactNode;
}) {
  const sizes = { xs: "relative size-5 after:absolute after:-inset-2.5 after:content-['']", sm: "size-7 text-xs", md: "size-9", lg: "size-11" }[size];
  const fills = {
    grey: "bg-fill text-muted hover:bg-fill-strong hover:text-fg",
    none: "text-muted hover:bg-fill hover:text-fg",
    white: "bg-surface text-muted shadow-sm ring-1 ring-line hover:bg-fill-subtle hover:text-fg",
    black: "bg-ink text-paper shadow-sm hover:bg-slate dark:bg-white dark:text-ink dark:hover:bg-zinc-200",
    red: "bg-danger text-white shadow-sm hover:opacity-90",
  }[fill];
  return (
    <button
      type="button"
      title={label}
      {...rest}
      aria-label={label}
      className={`grid shrink-0 place-items-center rounded-full transition active:scale-95 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 ${sizes} ${fills} ${className}`}
    >
      {children}
    </button>
  );
}
